import type { Movie, MediaType } from "@/types/movie";

export type TypeSlug = "movie" | "tv" | "variety";

export const slugToType: Record<TypeSlug, MediaType> = {
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
};

export const typeToSlug: Record<MediaType, TypeSlug> = {
  电影: "movie",
  电视剧: "tv",
  综艺: "variety",
};

export const typeSlugs: TypeSlug[] = ["movie", "tv", "variety"];

export function isValidTypeSlug(value: string | undefined): value is TypeSlug {
  return value === "movie" || value === "tv" || value === "variety";
}

export const regions: string[] = [
  "全部",
  "中国大陆",
  "中国香港",
  "美国",
  "韩国",
  "日本",
  "英国",
];

export const years: string[] = [
  "全部",
  "2025",
  "2024",
  "2023",
  "2022",
  "2021",
  "2020",
  "更早",
];

export const sortOptions = [
  { value: "latest", label: "最近更新" },
  { value: "score", label: "评分最高" },
  { value: "hot", label: "播放最多" },
  { value: "year", label: "年份最新" },
] as const;

export type SortValue = (typeof sortOptions)[number]["value"];

/** 子类型（按标签归类的演示维度） */
export const subTypes = [
  "全部",
  "科幻",
  "悬疑",
  "动作",
  "爱情",
  "都市",
  "古装",
  "音乐",
  "竞技",
  "美食",
];

export function filterMovies(
  list: Movie[],
  options: { region?: string; year?: string; subType?: string }
): Movie[] {
  const { region, year, subType } = options;
  return list.filter((m) => {
    if (region && region !== "全部" && m.region !== region) return false;
    if (year && year !== "全部") {
      if (year === "更早") {
        if (m.year > 2021) return false;
      } else if (String(m.year) !== year) {
        return false;
      }
    }
    if (subType && subType !== "全部" && !m.tags.includes(subType)) return false;
    return true;
  });
}

export function sortMovies(list: Movie[], sort: SortValue): Movie[] {
  const copy = [...list];
  switch (sort) {
    case "score":
      return copy.sort((a, b) => b.score - a.score);
    case "hot":
      return copy.sort((a, b) => b.views - a.views);
    case "year":
      return copy.sort((a, b) => b.year - a.year);
    case "latest":
    default:
      return copy.sort(
        (a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
      );
  }
}

export function searchMovies(list: Movie[], keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return list.filter((m) => {
    const haystack = [m.title, m.director, ...m.actors, ...m.tags, m.type, m.region]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelatedMovies(list: Movie[], current: Movie, limit = 6): Movie[] {
  const scored = list
    .filter((m) => m.id !== current.id)
    .map((m) => {
      let weight = 0;
      if (m.type === current.type) weight += 2;
      m.tags.forEach((t) => {
        if (current.tags.includes(t)) weight += 3;
      });
      if (m.director === current.director) weight += 5;
      return { movie: m, weight };
    })
    .sort((a, b) => b.weight - a.weight || b.movie.views - a.movie.views);
  return scored.slice(0, limit).map((s) => s.movie);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 亿`;
  return `${views} 万`;
}